import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { PageHeader, BrutalCard } from "../components/brutal";
import { Trophy, Flame, Users, TrendingUp, Star, Sparkles, Megaphone, Loader2 } from "lucide-react";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

const PERIODS = [
  { value: "all", label: "All time" },
  { value: "monthly", label: "This month" },
  { value: "weekly", label: "This week" },
];

// Distinct on-brand palette per team
const TEAM_THEME = {
  Kerry: { bg: "bg-brand-blue", text: "text-white", chip: "bg-white text-brand-blue", emoji: "💚" },
  "West Cork": { bg: "bg-brand-orange", text: "text-white", chip: "bg-white text-brand-orange", emoji: "🧡" },
};

const Stat = ({ label, value, icon, accent }) => (
  <div className="brutal bg-white/95 p-4 text-center">
    <div className={`w-10 h-10 mx-auto ${accent} border-2 border-black rounded-xl flex items-center justify-center text-white shadow-brutal mb-2`}>
      {icon}
    </div>
    <div className="text-3xl font-display font-black text-neutral-900 leading-none">{value}</div>
    <div className="text-[11px] uppercase tracking-widest font-bold text-neutral-700 mt-1">{label}</div>
  </div>
);

const TeamCard = ({ team, leading }) => {
  const theme = TEAM_THEME[team.team] || { bg: "bg-brand-ink", text: "text-white", chip: "bg-white text-black", emoji: "" };
  return (
    <div className={`brutal ${theme.bg} ${theme.text} p-6 sm:p-7 relative overflow-hidden`} data-testid={`team-card-${team.team.toLowerCase().replace(/\s+/g, "-")}`}>
      {leading && (
        <div className="absolute top-3 right-3 inline-flex items-center gap-1 bg-brand-yellow border-2 border-black rounded-lg px-2 py-1 shadow-brutal animate-pulse">
          <Trophy className="w-3.5 h-3.5 text-black" strokeWidth={2.6} />
          <span className="text-[10px] font-black uppercase text-black tracking-widest">Leading</span>
        </div>
      )}
      <div className="flex items-center gap-3">
        <span className="text-4xl leading-none">{theme.emoji}</span>
        <div>
          <h2 className="font-display font-black text-3xl sm:text-4xl leading-none">{team.team}</h2>
          <div className="text-xs font-semibold opacity-90 mt-1 uppercase tracking-widest">{team.headcount} {team.headcount === 1 ? "member" : "members"}</div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 mt-5">
        <Stat label="Points" value={team.total_points || 0} icon={<Star className="w-5 h-5" strokeWidth={2.5} />} accent="bg-brand-yellow text-black" />
        <Stat label="Referrals" value={team.total_referrals || 0} icon={<Users className="w-5 h-5" strokeWidth={2.5} />} accent="bg-brand-pink" />
        <Stat label="Hires" value={team.hired_referrals || 0} icon={<Flame className="w-5 h-5" strokeWidth={2.5} />} accent="bg-brand-green text-black" />
        <Stat label="Conv. rate" value={`${team.conversion_rate || 0}%`} icon={<TrendingUp className="w-5 h-5" strokeWidth={2.5} />} accent="bg-brand-blue" />
      </div>

      {team.top_performer ? (
        <div className={`brutal ${theme.chip} p-3 mt-4 flex items-center gap-3`}>
          <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-black bg-white flex items-center justify-center font-display font-black flex-shrink-0">
            {team.top_performer.avatar_url ? (
              <img src={team.top_performer.avatar_url} alt={team.top_performer.name} className="w-full h-full object-cover" />
            ) : (
              team.top_performer.name?.[0] || "?"
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest font-bold opacity-80">Top performer</div>
            <div className="font-display font-black truncate">{team.top_performer.name}</div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-display font-black leading-none">{team.top_performer.points}</div>
            <div className="text-[10px] uppercase tracking-widest font-bold opacity-80">pts</div>
          </div>
        </div>
      ) : (
        <div className={`brutal ${theme.chip} p-3 mt-4 text-center text-xs`}>
          No referrals yet — be the first to score for {team.team}!
        </div>
      )}
    </div>
  );
};

export default function RivalryPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [period, setPeriod] = useState("all");
  const [data, setData] = useState(null);
  const [postingRecap, setPostingRecap] = useState(false);

  useEffect(() => {
    api.get(`/rivalry?period=${period}`).then((r) => setData(r.data)).catch(() => setData({ teams: [] }));
  }, [period]);

  const postLastMonthRecap = async () => {
    setPostingRecap(true);
    try {
      const { data: res } = await api.post("/rivalry/recap");
      if (res.posted) {
        toast.success(`Recap posted to the shout-outs feed (${res.period})`);
      } else {
        toast(`No recap to post — ${res.reason}`, { icon: "ℹ️" });
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPostingRecap(false);
    }
  };

  const teams = data?.teams || [];
  const kerry = teams.find((t) => t.team === "Kerry");
  const westCork = teams.find((t) => t.team === "West Cork");
  const leader = teams.find((t) => t.is_leader);
  const tied = teams.length === 2 && teams[0].total_points === teams[1].total_points && teams[0].total_points > 0;
  const everyoneZero = teams.every((t) => !t.total_points);

  return (
    <div>
      <PageHeader
        title="Kerry vs West Cork"
        subtitle="Friendly rivalry, real points. Whichever team racks up more wins this month gets the crown."
      />

      <div className="flex items-center justify-between gap-3 flex-wrap mb-6">
        <div className="brutal p-1 inline-flex gap-1" data-testid="rivalry-period-tabs">
          {PERIODS.map((p) => (
            <button
              key={p.value}
              onClick={() => setPeriod(p.value)}
              className={`px-3 py-1.5 rounded-lg text-sm font-semibold ${period === p.value ? "bg-brand-yellow" : "hover:bg-neutral-100"}`}
              data-testid={`period-${p.value}`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {isAdmin && (
          <button
            onClick={postLastMonthRecap}
            disabled={postingRecap}
            className="btn-brutal btn-white text-sm"
            title="Post last month's Kerry vs West Cork recap to the shout-outs feed"
            data-testid="post-rivalry-recap-btn"
          >
            {postingRecap ? <Loader2 className="w-4 h-4 animate-spin" /> : <Megaphone className="w-4 h-4" />}
            Post last month's recap
          </button>
        )}
      </div>

      {everyoneZero ? (
        <BrutalCard className="p-8 text-center mb-6 bg-brand-yellow">
          <Sparkles className="w-8 h-8 mx-auto mb-2" strokeWidth={2.5} />
          <h3 className="font-display font-black text-xl">No points scored yet — game on!</h3>
          <p className="text-sm text-neutral-700 mt-1">Refer your first candidate to put your team on the board.</p>
        </BrutalCard>
      ) : tied ? (
        <BrutalCard className="p-4 text-center mb-6 bg-brand-yellow">
          <div className="text-sm font-bold">It's a tie! 🤝 Both teams on {teams[0].total_points} points.</div>
        </BrutalCard>
      ) : leader ? (
        <BrutalCard className="p-4 text-center mb-6 bg-brand-green">
          <div className="text-sm font-bold inline-flex items-center gap-2">
            <Trophy className="w-4 h-4" /> {leader.team} is leading with {leader.total_points} points
          </div>
        </BrutalCard>
      ) : null}

      <div className="grid md:grid-cols-2 gap-5 items-start">
        {kerry && <TeamCard team={kerry} leading={kerry.is_leader} />}
        {westCork && <TeamCard team={westCork} leading={westCork.is_leader} />}
      </div>

      {teams.length === 0 && data && (
        <BrutalCard className="p-8 text-center">
          <p className="text-sm text-neutral-600">No data yet. Add employees and assign them to a team.</p>
        </BrutalCard>
      )}
    </div>
  );
}
